"use client";

import { useRef, useState } from "react";
import { ErrorMessage, Field, Form, Formik, FormikHelpers } from "formik";
import { toast } from "react-toastify";
import { countries } from "country-list-json";
import { ReCAPTCHA } from "@/lib/vendor";
import * as yup from "yup";
import apiService from "@/common/services/apiService";
import { useRouter } from "next/router";
import { hideFormLoader, showFormLoader } from "@/store/loaderSlice";
import { useDispatch } from "react-redux";

type FormValues = {
  name: string;
  business_email: string;
  industry: string;
  company_name: string;
  designation: string;
  country_name: string;
  message: string;
};

type ModalProps = {
  show: boolean;
  handleClose: () => void;
};

const GetBusinessModal = ({ show, handleClose }: ModalProps) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const reCaptchaRef = useRef<ReCAPTCHA | null>(null);
  const [captchaError, setCaptchaError] = useState<string>("");

  if (!show) return null;

  const sitekey: string = process.env.NEXT_PUBLIC_RECAPTCHA_V3 || "";

  const getTheme =
    typeof window !== "undefined" && localStorage.getItem("Theme") === "dark"
      ? "dark"
      : "light";

  const validateCaptchaV3 = (): Promise<string | null> => {
    return new Promise((resolve) => {
      reCaptchaRef.current?.executeAsync()?.then((value) => {
        resolve(value ?? null);
      });
    });
  };

  const handleSubmit = async (
    formData: FormValues,
    { resetForm }: FormikHelpers<FormValues>,
  ) => {
    const isHuman = await validateCaptchaV3();
    if (!isHuman) {
      setCaptchaError("Invalid captcha.");
      return;
    }

    dispatch(showFormLoader());
    setCaptchaError("");

    const payload = {
      ...formData,
      captcha_token_v3: isHuman,
      page_link: window.location.href,
      page_title: document.title,
    };

    try {
      const lst: any = await apiService.post("business-model", { data: payload });

      dispatch(hideFormLoader());
      resetForm();
      reCaptchaRef.current?.reset();

      if (lst.data.success) {
        router.push("/thank-you");
        handleClose();
      } else {
        toast.error(lst.data.message);
      }
    } catch (error: any) {
      dispatch(hideFormLoader());
      toast.error(error?.message || "Something went wrong");
    }
  };

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-lg w-full max-w-3xl relative max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center border-b px-6 py-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Get business model
          </h2>
          <button
            onClick={handleClose}
            className="text-2xl leading-none text-gray-600 dark:text-gray-300 hover:text-black"
          >
            ×
          </button>
        </div>

        <Formik<FormValues>
          initialValues={{
            name: "",
            business_email: "",
            industry: "",
            company_name: "",
            designation: "",
            country_name: "",
            message: "",
          }}
          validationSchema={yup.object({
            name: yup.string().required("Name is required."),
            business_email: yup
              .string()
              .email("Invalid email.")
              .required("Email is required."),
            industry: yup.string().required("Industry is required."),
            company_name: yup.string().required("Company Name is required."),
            designation: yup.string().required("Designation is required."),
            country_name: yup.string().required("Country Name is required."),
            message: yup.string().required("Message is required."),
          })}
          onSubmit={handleSubmit}
        >
          {() => (
            <Form>
              {/* Body */}
              <div className="px-6 py-5 space-y-6">
                <p className="text-gray-700 dark:text-gray-300 text-sm">
                  We believe in connecting personally, kindly drop your details
                  below and we will connect with you soon via email with the
                  precise information requested.
                </p>

                {/* 6 Inputs */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { label: "Name *", name: "name" },
                    {
                      label: "Business Email *",
                      name: "business_email",
                      type: "email",
                    },
                    { label: "Industry *", name: "industry" },
                    { label: "Company Name *", name: "company_name" },
                    { label: "Designation *", name: "designation" },
                  ].map((inp, idx) => (
                    <div key={idx}>
                      <label className="block text-sm font-medium mb-1">
                        {inp.label}
                      </label>
                      <Field
                        type={inp.type || "text"}
                        name={inp.name}
                        className="w-full border rounded-lg px-3 py-2 dark:bg-gray-800 dark:border-gray-700"
                      />
                      <ErrorMessage
                        name={inp.name}
                        component="span"
                        className="text-red-500 text-sm"
                      />
                    </div>
                  ))}

                  {/* Country */}
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Country *
                    </label>
                    <Field
                      as="select"
                      name="country_name"
                      className="w-full border rounded-lg px-3 py-2 dark:bg-gray-800 dark:border-gray-700"
                    >
                      <option value="">Select Country</option>
                      {countries.map((c) => (
                        <option key={c.code} value={c.name}>
                          {c.name}
                        </option>
                      ))}
                    </Field>
                    <ErrorMessage
                      name="country_name"
                      component="span"
                      className="text-red-500 text-sm"
                    />
                  </div>

                  {/* Message */}
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium mb-1">
                      Message *
                    </label>
                    <Field
                      as="textarea"
                      name="message"
                      rows={4}
                      className="w-full border rounded-lg px-3 py-2 dark:bg-gray-800 dark:border-gray-700"
                    />
                    <ErrorMessage
                      name="message"
                      component="span"
                      className="text-red-500 text-sm"
                    />
                  </div>

                  {/* Captcha */}
                  <div className="sm:col-span-2 flex flex-col">
                    <ReCAPTCHA
                      style={{ display: "inline" }}
                      theme={getTheme}
                      size="invisible"
                      sitekey={sitekey}
                      ref={reCaptchaRef}
                    />
                    {captchaError && (
                      <span className="text-red-500 text-sm mt-1">
                        {captchaError}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 border-t px-6 py-4 bg-gray-50 dark:bg-gray-800">
                <button
                  type="button"
                  className="px-4 py-2 rounded border dark:border-gray-700 hover:bg-gray-200 dark:hover:bg-gray-700"
                  onClick={handleClose}
                >
                  Close
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Submit
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default GetBusinessModal;
